/**
 * Layout components and auth guards for EventFlow
 */
import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore, useRealtimeStore } from '@/lib/store';
import { churchesAPI, cmsAPI } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { WorkspaceSwitcher } from '@/components/WorkspaceSwitcher';
import NotificationBell from '@/components/admin/NotificationBell';
import { getToken } from '@/lib/authToken';
import { 
  ChevronLeft, Wifi, WifiOff, LogOut, Loader2, User, Settings,
  Church, MapPin, Phone, Mail, ExternalLink, ChevronDown, Menu, X, FileText, Lock,
  Image as ImageIcon, Home as HomeIcon, UtensilsCrossed, Sparkles, GraduationCap
} from 'lucide-react';
import { isPlatformTier } from '@/lib/roleTier';
import homeFor from '@/lib/homeFor';
import { requiredTierFor } from '@/lib/adminRouteTiers';
import RoleGuard from '@/components/RoleGuard';

// ============== Auth Guard ==============

/**
 * iter 624 — the loop breaker.
 *
 * Both redirects below send a person to `homeFor(...)`. When that answer is
 * the route they are already on, the redirect re-renders this guard, which
 * redirects again, forever: that is how a Pastor landing on the `superOnly`
 * `/admin` got an endless bounce rather than a page.
 *
 * The map in `lib/homeFor.js` is fixed so it no longer produces those
 * answers, but a map and a route table drift apart — they already had, in
 * four places. So nothing here redirects to where it already is. If the only
 * answer is the current path, say so on screen instead; a readable message is
 * always better than a spin, and it names the real problem for whoever has to
 * fix the map.
 */
const NoWorkspaceNotice = ({ pathname }) => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="no-workspace-notice">
    <div className="max-w-md text-center">
      <div className="mx-auto h-12 w-12 rounded-full bg-slate-200 flex items-center justify-center mb-4">
        <Lock className="h-5 w-5 text-slate-600" />
      </div>
      <h1 className="text-xl font-bold text-slate-900">This page is not open to your account</h1>
      <p className="text-sm text-slate-600 mt-2">
        You are signed in, but <span className="font-mono">{pathname}</span> needs a level of access
        your account does not hold — and it is also where we would normally send you, so there is
        nowhere to pass you on to. Ask an administrator to check the role on your account.
      </p>
      <Link to="/home" className="inline-block mt-5">
        <Button variant="outline" size="sm" data-testid="no-workspace-home">Go to my dashboard</Button>
      </Link>
    </div>
  </div>
);

const PlatformOnlyNotice = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="platform-only-notice">
    <div className="max-w-md text-center">
      <div className="mx-auto h-12 w-12 rounded-full bg-slate-200 flex items-center justify-center mb-4">
        <Lock className="h-5 w-5 text-slate-600" />
      </div>
      <h1 className="text-xl font-bold text-slate-900">Platform admins only</h1>
      <p className="text-sm text-slate-600 mt-2">
        This view covers every organisation's money, so it is limited to Super Admins and
        Platform Admins. Your own finance data is available in your organisation's workspace.
      </p>
      <Link to="/admin" className="inline-block mt-5">
        <Button variant="outline" size="sm" data-testid="platform-only-back">Back to dashboard</Button>
      </Link>
    </div>
  </div>
);

export const RequireAuth = ({ children, type = "any", superOnly = false, platformOnly = false }) => {
  const { token, userType, user } = useAuthStore();
  const location = useLocation();
  
  const hasHydratedRef = React.useRef(useAuthStore.getState()._hasHydrated);
  const [isReady, setIsReady] = useState(false);
  
  React.useLayoutEffect(() => {
    const unsubscribe = useAuthStore.subscribe((state) => {
      if (state._hasHydrated && !hasHydratedRef.current) {
        hasHydratedRef.current = true;
        setIsReady(true);
      }
    });
    
    // Check if already hydrated
    if (useAuthStore.getState()._hasHydrated) {
      hasHydratedRef.current = true;
      setIsReady(true);
    }
    
    // Fallback timeout - but also check localStorage directly
    const timeout = setTimeout(() => {
      if (!hasHydratedRef.current) {
        hasHydratedRef.current = true;
        setIsReady(true);
      }
    }, 200);
    
    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  // Show loading while waiting for hydration
  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Check store token, localStorage fallback, AND the View-As preview
  // session token. The school portal's "view-as student" feature stores the
  // preview JWT in sessionStorage only (so it never pollutes the admin's
  // real session). Without this check, every <RequireAuth> route — most
  // notably the Bible reading-plan reader — kicks the admin-as-student
  // straight back to /login, producing the "scriptures redirect to Events
  // login" symptom.
  const storeToken = token;
  const localToken = getToken();
  let previewToken = '';
  try {
    previewToken = sessionStorage.getItem('school_preview_token') || '';
  } catch (_e) { /* sessionStorage unavailable */ }
  const hasValidToken = storeToken || localToken || previewToken;

  if (!hasValidToken) {
    // Pick the right login portal based on URL hints so school students
    // never land on the Admin/Member/Delegate sign-in. If the path itself
    // is school-scoped (`/school/:tenantId/...`) or the bible plan was
    // opened with `?from=school&scopeId=<tenantId>`, redirect to that
    // tenant's student login with a `redirect` back to the original URL.
    const path = location.pathname || '';
    const search = location.search || '';
    let loginTarget = '/login';

    const schoolMatch = path.match(/^\/school\/([^/]+)/);
    if (schoolMatch) {
      const tid = schoolMatch[1];
      loginTarget = `/school/${tid}/login?redirect=${encodeURIComponent(path + search)}`;
    } else {
      const sp = new URLSearchParams(search);
      const from = sp.get('from');
      const scopeId = sp.get('scopeId');
      if (from === 'school' && scopeId) {
        loginTarget = `/school/${scopeId}/login?redirect=${encodeURIComponent(path + search)}`;
      } else if (from === 'event' && scopeId) {
        loginTarget = `/event/${scopeId}/portal/login?redirect=${encodeURIComponent(path + search)}`;
      }
    }

    return <Navigate to={loginTarget === '/login'
      ? `/login?redirect=${encodeURIComponent(path + search)}`
      : loginTarget} state={{ from: location }} replace />;
  }

  // For type-specific routes, check userType from store.
  // A signed-in person is sent to their OWN home — never to the public
  // landing page, which used to read as "login threw me out" (iter 580).
  //
  // iter 622 — an office holder who signs in through the member portal carries
  // `userType === "member"` while the backend's canonical office gate treats
  // them as a church admin. That disagreement bounced every appointment card
  // to the member portal, which carries a church finder: the reported
  // "all-churches page". The server asserts `is_church_admin` on the session
  // user, so honour it here. This is navigation only — every endpoint behind
  // the route still checks capability and unit scope for itself.
  const opensWorkspace = user?.is_church_admin === true;

  // iter 624 — never bounce somebody to the page they are already on. See
  // NoWorkspaceNotice above.
  const sendHome = () => {
    const target = homeFor(userType, user);
    if (target === location.pathname) {
      return <NoWorkspaceNotice pathname={location.pathname} />;
    }
    return <Navigate to={target} replace />;
  };

  const typeMismatch = type !== "any" && userType && userType !== type
    && !(type === "admin" && opensWorkspace);
  if (typeMismatch) {
    return sendHome();
  }

  // Platform-tier routes (Revenue Dashboard) — platform-wide money, so only
  // super_admin / platform_admin. Event-scoped admins are NOT waved through
  // here the way `superOnly` allows.
  if (platformOnly) {
    if (!isPlatformTier(user)) {
      return <PlatformOnlyNotice />;
    }
    return children;
  }

  // Super-admin-only routes — scoped tenant admins (school admins, future event/church
  // admins) get redirected to their own dashboard so they never see the platform-wide
  // admin tiles that belong to another organisation.
  if (superOnly && user) {
    const isSuper = user.is_super_admin === true
      || user.role === 'super_admin'
      || (Array.isArray(user.permissions) && (user.permissions.includes('*') || user.permissions.includes('super_admin')));
    // An admin invited onto a single event belongs on /admin — the dashboard
    // renders the event-only view for them (see EventScopedDashboard).
    const isEventScoped = user.event_scope?.is_event_scoped
      || (user.role === 'event_team'
          && ((user.assigned_events?.length || 0)
              + Object.keys(user.event_roles || {}).length) > 0);
    if (!isSuper && !isEventScoped) {
      if (user.tenant_id && location.pathname !== `/admin/schools/${user.tenant_id}`) {
        return <Navigate to={`/admin/schools/${user.tenant_id}`} replace />;
      }
      // Unknown scope — send them to their own workspace, not the public site,
      // and not back to this same page (iter 624).
      return sendHome();
    }
  }

  // Every /admin/* route is tier-gated from the canonical registry (iter 570).
  // RoleGuard renders a readable "you need X" panel rather than a blank page
  // full of 403s. Explicit superOnly/platformOnly routes keep their own rule.
  const requiredTier = (userType === 'admin' || type === 'admin')
    ? requiredTierFor(location.pathname)
    : null;
  if (requiredTier) {
    return <RoleGuard minTier={requiredTier}>{children}</RoleGuard>;
  }

  return children;
};

// ============== Public Layout with Header and Footer ==============
export const PublicLayout = ({ children, hideHeader = false, hideFooter = false }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fetch app config for branding
  const { data: appConfig } = useQuery({
    queryKey: ['app-config'],
    queryFn: async () => {
      try {
        const response = await api.get('/app-config');
        return response.data;
      } catch {
        return null;
      }
    },
    staleTime: 60000 * 5
  });

  // Fetch module visibility settings
  const { data: moduleVisibility } = useQuery({
    queryKey: ['module-visibility'],
    queryFn: async () => {
      try {
        const response = await api.get('/module-visibility');
        return response.data;
      } catch {
        return null;
      }
    },
    staleTime: 60000 * 5
  });

  // Helper function to check if module is visible
  const isModuleVisible = (moduleId, location = 'all') => {
    const settings = moduleVisibility?.[moduleId];
    if (!settings) return true; // Default to visible if no settings
    if (!settings.visible) return false;
    if (location === 'nav') return settings.show_in_nav !== false;
    if (location === 'quick_access') return settings.show_in_quick_access !== false;
    if (location === 'footer') return settings.show_in_footer !== false;
    return true;
  };

  // Check if module is in maintenance mode
  const isModuleMaintenance = (moduleId) => {
    const settings = moduleVisibility?.[moduleId];
    return settings?.maintenance_mode === true;
  };

  const getMaintenanceMessage = (moduleId) => {
    const settings = moduleVisibility?.[moduleId];
    return settings?.maintenance_message || 'This section is under maintenance. Please check back later.';
  };

  // Fetch churches for header dropdown and footer
  const { data: churches } = useQuery({
    queryKey: ['public-churches'],
    queryFn: () => churchesAPI.list().then(r => r.data),
    staleTime: 60000 * 5
  });

  // Fetch CMS pages for navigation
  const { data: pages } = useQuery({
    queryKey: ['public-pages'],
    queryFn: () => cmsAPI.listPages().then(r => r.data),
    staleTime: 60000 * 5
  });

  const logoUrl = appConfig?.logo_url 
    ? (appConfig.logo_url.startsWith('http') ? appConfig.logo_url : `${process.env.REACT_APP_BACKEND_URL}${appConfig.logo_url}`)
    : null;
  // A logo that fails to load used to leave a broken-image box with the alt
  // text beside it, which makes the whole site look broken when object storage
  // is unavailable. Fall back to the wordmark instead (iter 702).
  const [logoBroken, setLogoBroken] = React.useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Public Header */}
      {!hideHeader && (
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Logo/Brand */}
              <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
                {logoUrl && !logoBroken ? (
                  <img src={logoUrl} alt={appConfig?.app_name || 'EventFlow'}
                       className="h-8 w-auto" onError={() => setLogoBroken(true)} />
                ) : (
                  <span className="text-xl font-bold text-slate-900">
                    {appConfig?.app_name || 'EventFlow'}
                  </span>
                )}
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-6">
                {/* CMS Pages */}
                {pages?.length > 0 && pages.slice(0, 4).map(page => (
                  <Link
                    key={page.id}
                    to={`/page/${page.slug}`}
                    className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    {page.title}
                  </Link>
                ))}

                {/* Churches Dropdown - Only show if visible */}
                {isModuleVisible('churches', 'nav') && churches?.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="gap-1">
                        <Church className="h-4 w-4" />
                        Churches
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72">
                      <DropdownMenuLabel>Our Churches</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {churches.slice(0, 8).map(church => (
                        <DropdownMenuItem
                          key={church.id}
                          className="flex items-start gap-3 py-2"
                          onClick={() => church.website_url && window.open(church.website_url, '_blank')}
                        >
                          {church.logo_url ? (
                            <img
                              src={church.logo_url.startsWith('http') ? church.logo_url : `${process.env.REACT_APP_BACKEND_URL}${church.logo_url}`}
                              alt={church.name}
                              className="h-8 w-8 rounded object-contain"
                              onError={(e) => {
                                // Show the church icon rather than a broken box.
                                e.currentTarget.style.display = 'none';
                                if (e.currentTarget.nextElementSibling) {
                                  e.currentTarget.nextElementSibling.style.display = 'flex';
                                }
                              }}
                            />
                          ) : null}
                          <div
                            className="h-8 w-8 bg-slate-100 rounded items-center justify-center"
                            style={{ display: church.logo_url ? 'none' : 'flex' }}
                          >
                            <Church className="h-4 w-4 text-slate-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{church.name}</p>
                            {church.location && (
                              <p className="text-xs text-slate-500 truncate">{church.location}</p>
                            )}
                          </div>
                          {church.website_url && (
                            <ExternalLink className="h-3 w-3 text-slate-400" />
                          )}
                        </DropdownMenuItem>
                      ))}
                      {churches.length > 8 && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => navigate('/churches')}>
                            View all {churches.length} churches
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/churches')}>
                        <Church className="h-4 w-4 mr-2" />
                        Churches Directory
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                <Button variant="outline" size="sm" onClick={() => navigate('/login')}>
                  Sign In
                </Button>
              </nav>

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>

            {/* Mobile Navigation */}
            {mobileMenuOpen && (
              <div className="md:hidden py-4 border-t">
                <nav className="flex flex-col gap-2">
                  {pages?.map(page => (
                    <Link
                      key={page.id}
                      to={`/page/${page.slug}`}
                      className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {page.title}
                    </Link>
                  ))}
                  {churches?.length > 0 && (
                    <Link
                      to="/churches"
                      className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Church className="h-4 w-4" />
                      Churches Directory
                    </Link>
                  )}
                  <Button 
                    variant="outline" 
                    className="mt-2"
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                  >
                    Sign In
                  </Button>
                </nav>
              </div>
            )}
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Public Footer */}
      {!hideFooter && (
        <footer className="bg-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Brand */}
              <div className="md:col-span-1">
                {logoUrl ? (
                  <img src={logoUrl} alt={appConfig?.app_name || 'EventFlow'} className="h-10 w-auto brightness-0 invert mb-4" />
                ) : (
                  <h3 className="text-xl font-bold mb-4">{appConfig?.app_name || 'EventFlow'}</h3>
                )}
                <p className="text-sm text-slate-400">
                  AI-Native Event Management Platform
                </p>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="font-semibold mb-4">Quick Links</h4>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                  <li><Link to="/login" className="hover:text-white transition-colors">Sign In</Link></li>
                  {pages?.slice(0, 4).map(page => (
                    <li key={page.id}>
                      <Link to={`/page/${page.slug}`} className="hover:text-white transition-colors">
                        {page.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Churches */}
              {churches?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <Church className="h-4 w-4" />
                    Our Churches
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-400">
                    {churches.slice(0, 5).map(church => (
                      <li key={church.id}>
                        {church.website_url ? (
                          <a
                            href={church.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-white transition-colors flex items-center gap-1"
                          >
                            {church.name}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span>{church.name}</span>
                        )}
                      </li>
                    ))}
                    {churches.length > 5 && (
                      <li>
                        <Link to="/churches" className="text-blue-400 hover:text-blue-300 transition-colors">
                          View all churches →
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Contact */}
              <div>
                <h4 className="font-semibold mb-4">Contact</h4>
                <ul className="space-y-2 text-sm text-slate-400">
                  {appConfig?.support_email && (
                    <li className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      <a href={`mailto:${appConfig.support_email}`} className="hover:text-white transition-colors">
                        {appConfig.support_email}
                      </a>
                    </li>
                  )}
                  {appConfig?.app_url && (
                    <li className="flex items-center gap-2">
                      <ExternalLink className="h-4 w-4" />
                      <a href={appConfig.app_url} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                        {appConfig.app_url.replace(/^https?:\/\//, '')}
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            {/* Copyright */}
            <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-500">
              <p>© {new Date().getFullYear()} {appConfig?.app_name || 'EventFlow'}. All rights reserved.</p>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

// ============== Dashboard Layout ==============
export const DashboardLayout = ({ children, title, showBackButton = false }) => {
  const { user, userType, logout } = useAuthStore();
  const { connected } = useRealtimeStore();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center gap-2 min-h-16 py-2">
            <div className="flex min-w-0 basis-full sm:basis-auto items-center gap-2 sm:gap-4">
              {showBackButton && (
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              )}
              <h1 className="truncate text-base sm:text-xl font-semibold text-slate-900">{title}</h1>
            </div>
            <div className="flex shrink-0 items-center gap-1.5 sm:gap-4">
              {/* iter-438k Phase 5 — Workspace switcher (multi-role users) */}
              <WorkspaceSwitcher />
              {/* Connection status */}
              <div className="flex items-center gap-2">
                {connected ? (
                  <Wifi className="h-4 w-4 text-green-500" />
                ) : (
                  <WifiOff className="h-4 w-4 text-slate-400" />
                )}
              </div>
              {/* Language selector */}
              <LanguageSwitcher variant="minimal" />
              {/* Where an alert lands — the same bell on every admin screen */}
              {userType !== 'delegate' ? <NotificationBell /> : null}
              {/* User dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback>{user?.name?.[0] || user?.first_name?.[0] || "U"}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium hidden sm:inline">
                      {user?.name || `${user?.first_name || ""} ${user?.last_name || ""}`}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {userType}
                    </Badge>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {userType === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/profile')} data-testid="profile-menu-item">
                      <User className="mr-2 h-4 w-4" />
                      Profile Settings
                    </DropdownMenuItem>
                  )}
                  {userType === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/settings')} data-testid="settings-menu-item">
                      <Settings className="mr-2 h-4 w-4" />
                      System Settings
                    </DropdownMenuItem>
                  )}
                  {userType === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/heroes')} data-testid="site-appearance-menu-item">
                      <ImageIcon className="mr-2 h-4 w-4" />
                      Site Appearance
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Staff Portals</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => navigate('/housing/staff')} data-testid="housing-staff-portal-menu-item">
                    <HomeIcon className="mr-2 h-4 w-4" />
                    Housing Staff
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dining/pos')} data-testid="dining-pos-portal-menu-item">
                    <UtensilsCrossed className="mr-2 h-4 w-4" />
                    Dining POS
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Community</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => navigate('/ai-tutor')} data-testid="ai-tutor-menu-item">
                    <Sparkles className="mr-2 h-4 w-4" />
                    AI Tutor
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/alumni-portal')} data-testid="alumni-portal-menu-item">
                    <GraduationCap className="mr-2 h-4 w-4" />
                    Alumni Portal
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="logout-menu-item">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};

export default { RequireAuth, PublicLayout, DashboardLayout };
